import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  BookOpen,
  BookOpenText,
  CalendarOff,
  CalendarPlus,
  ChevronDown,
  CirclePlay,
  FileText,
  Filter,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  SquarePen,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";

import {
  activityFormatSchema,
  scheduleDateSchema,
  type ActivityFormat,
  type ActivitySummary,
  type CollectionSummary,
  type LibraryQuery,
} from "@learn-flow/contracts";

import { AppShell } from "../../components/app-shell.js";
import { FlashcardIndicator } from "../../components/flashcard-indicator.js";
import { PageHeaderCreateAction } from "../../components/page-header-create-action.js";
import { createActivityReturnState } from "../../lib/activity-return-context.js";
import { ApiError } from "../../lib/api.js";
import {
  deleteCollection,
  libraryQueryKey,
  libraryQueryRoot,
  loadLibrary,
  permanentlyDeleteActivity,
  renameCollection,
  setActivityArchived,
} from "./library-api.js";
import { storeLibraryContext } from "./library-context.js";
import { todayQueryRoot } from "../today/today-api.js";
import { ArchiveActivityDialog } from "./archive-activity-dialog.js";

export function LibraryPage(): React.JSX.Element {
  const [parameters, setSearchParameters] = useSearchParams();
  const parametersRef = useRef(parameters);
  const location = useLocation();
  const queryClient = useQueryClient();
  const query = parseLibraryQuery(parameters);
  const requestedScheduleDate = scheduleDateSchema.safeParse(parameters.get("scheduleDate")).success
    ? parameters.get("scheduleDate")
    : null;
  const [searchValue, setSearchValue] = useState(query.search);
  const [showFilters, setShowFilters] = useState(
    Boolean(query.collectionId || query.format || query.difficulty),
  );
  const [collectionDialog, setCollectionDialog] = useState<CollectionSummary | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<CollectionSummary | null>(null);
  const [archiveDialog, setArchiveDialog] = useState<ActivitySummary | null>(null);
  const [activityDeleteDialog, setActivityDeleteDialog] = useState<ActivitySummary | null>(null);
  const [createdActivityId] = useState<string | null>(() => activityCreatedId(location.state));
  const [archiveFailure, setArchiveFailure] = useState<{
    id: string;
    title: string;
    action: "archive" | "restore";
  } | null>(null);
  const collectionFilter = useRef<HTMLSelectElement>(null);
  const archivedActivitiesButton = useRef<HTMLButtonElement>(null);

  const updateParameters = useCallback(
    (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(parametersRef.current);
      for (const [name, value] of Object.entries(updates)) {
        if (value) next.set(name, value);
        else next.delete(name);
      }
      parametersRef.current = next;
      setSearchParameters(next, { replace: true });
    },
    [setSearchParameters],
  );

  useEffect(() => {
    document.title = "Library · MemoKite";
  }, []);
  useEffect(() => {
    parametersRef.current = parameters;
  }, [parameters]);
  useEffect(() => {
    setSearchValue(query.search);
  }, [query.search]);
  useEffect(() => {
    if (searchValue === query.search) return;
    const timer = window.setTimeout(() => {
      updateParameters({ search: searchValue.trim() || null });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [query.search, searchValue, updateParameters]);
  useEffect(() => {
    storeLibraryContext(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);
  useLayoutEffect(() => {
    if (!createdActivityId) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [createdActivityId]);
  useEffect(() => {
    if (!activityCreatedId(location.state)) return;
    window.history.replaceState({ ...window.history.state, usr: null }, "");
  }, [location.state]);

  const library = useQuery({
    queryKey: libraryQueryKey(query),
    queryFn: () => loadLibrary(query),
    placeholderData: (previous) => previous,
  });
  const restoreMutation = useMutation({
    mutationFn: ({ id }: { id: string; title: string; action: "restore" }) =>
      setActivityArchived(id, "restore"),
    onMutate: () => setArchiveFailure(null),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: libraryQueryRoot }),
        queryClient.invalidateQueries({ queryKey: todayQueryRoot }),
      ]);
    },
    onError: (_error, variables) => setArchiveFailure(variables),
  });
  const returnContext = `${location.pathname}${location.search}`;
  const activeCollection =
    query.view === "active"
      ? library.data?.collections.find((collection) => collection.id === query.collectionId)
      : undefined;
  const activeLabel =
    query.view === "archived" ? "Archived" : (activeCollection?.name ?? "All activities");
  const hasFilters = Boolean(query.collectionId || query.format || query.difficulty);

  return (
    <AppShell mainClassName="library-screen">
      <header className="topbar library-topbar">
        <div>
          <h1>Learning library</h1>
        </div>
        <PageHeaderCreateAction label="New activity" state={{ libraryReturnTo: returnContext }} />
      </header>

      <div className="library-intro">
        <div className="library-tools">
          <label className="search-box">
            <Search size={17} aria-hidden="true" />
            <input
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search your library"
              aria-label="Search your library"
            />
            {searchValue ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  setSearchValue("");
                  updateParameters({ search: null });
                }}
              >
                <X size={15} />
              </button>
            ) : null}
          </label>
          <button
            type="button"
            className={`soft-button ${showFilters ? "active" : ""}`}
            aria-expanded={showFilters}
            aria-controls="library-filters"
            onClick={() => setShowFilters((value) => !value)}
          >
            <Filter size={16} />
            Filters
          </button>
          <button
            type="button"
            className={`soft-button ${query.unscheduled ? "active" : ""}`}
            aria-pressed={query.unscheduled ?? false}
            onClick={() => updateParameters({ unscheduled: query.unscheduled ? null : "true" })}
          >
            <CalendarOff size={16} />
            Unscheduled
          </button>
          <button
            ref={archivedActivitiesButton}
            type="button"
            className={`soft-button ${query.view === "archived" ? "active" : ""}`}
            aria-pressed={query.view === "archived"}
            onClick={() =>
              updateParameters({
                view: query.view === "archived" ? null : "archived",
                collectionId: null,
              })
            }
          >
            <Archive size={16} />
            Archived <span>{library.data?.counts.archived ?? "—"}</span>
          </button>
        </div>
      </div>

      {showFilters ? (
        <div className="filter-bar" id="library-filters">
          <span>Showing</span>
          <FilterSelect
            label="Collection"
            selectRef={collectionFilter}
            value={query.collectionId ?? ""}
            onChange={(value) => updateParameters({ view: null, collectionId: value || null })}
            options={[
              ["", "All collections"],
              ...(library.data?.collections.map(
                (collection) => [collection.id, collection.name] as const,
              ) ?? []),
            ]}
          />
          <FilterSelect
            label="Format"
            value={query.format ?? ""}
            onChange={(value) => updateParameters({ format: value || null })}
            options={[
              ["", "All formats"],
              ...activityFormatSchema.options.map(
                (format) => [format, formatLabel(format)] as const,
              ),
            ]}
          />
          <FilterSelect
            label="Difficulty"
            value={query.difficulty?.toString() ?? ""}
            onChange={(value) => updateParameters({ difficulty: value || null })}
            options={[
              ["", "Any difficulty"],
              ["unrated", "Unrated"],
              ...[1, 2, 3, 4, 5].map(
                (difficulty) => [difficulty.toString(), `${difficulty.toString()} of 5`] as const,
              ),
            ]}
          />
          <button
            type="button"
            className="clear-filter"
            disabled={!hasFilters}
            onClick={() => updateParameters({ collectionId: null, format: null, difficulty: null })}
          >
            Clear filters (keep search)
          </button>
        </div>
      ) : null}

      <div className="library-layout">
        <section className="library-content" aria-labelledby="library-results-title">
          <div className="library-content-heading">
            <div>
              <h2 id="library-results-title">{activeLabel}</h2>
              <span aria-live="polite">
                {library.isFetching && library.data
                  ? "Updating results…"
                  : `${(library.data?.activities.length ?? 0).toString()} activities`}
              </span>
            </div>
            {activeCollection ? (
              <div className="collection-management" aria-label={`Manage ${activeCollection.name}`}>
                <button
                  type="button"
                  className="soft-button"
                  aria-label={`Rename ${activeCollection.name}`}
                  onClick={() => setCollectionDialog(activeCollection)}
                >
                  <Pencil size={14} />
                  Rename
                </button>
                <button
                  type="button"
                  className="soft-button collection-delete-button"
                  aria-label={`Delete ${activeCollection.name}`}
                  onClick={() => setDeleteDialog(activeCollection)}
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            ) : null}
          </div>

          {archiveFailure ? (
            <ActivityArchiveError
              title={archiveFailure.title}
              action={archiveFailure.action}
              onRetry={() => {
                if (archiveFailure.action === "restore") {
                  restoreMutation.mutate({ ...archiveFailure, action: "restore" });
                }
              }}
            />
          ) : null}

          {library.isLoading ? (
            <LibraryLoading />
          ) : library.isError ? (
            <LibraryErrorState onRetry={() => void library.refetch()} />
          ) : (library.data?.activities.length ?? 0) > 0 ? (
            <div className="activity-list">
              {library.data?.activities.map((activity) => (
                <ActivityRow
                  key={activity.id}
                  activity={activity}
                  returnContext={returnContext}
                  requestedScheduleDate={requestedScheduleDate}
                  isArchivedView={query.view === "archived"}
                  isNewlyCreated={activity.id === createdActivityId}
                  isPending={
                    restoreMutation.isPending && restoreMutation.variables.id === activity.id
                  }
                  onArchive={() => setArchiveDialog(activity)}
                  onRestore={() =>
                    restoreMutation.mutate({
                      id: activity.id,
                      title: activity.title,
                      action: "restore",
                    })
                  }
                  onDelete={() => setActivityDeleteDialog(activity)}
                />
              ))}
            </div>
          ) : (
            <LibraryEmptyState
              hasCriteria={Boolean(
                query.search ||
                  query.collectionId ||
                  query.format ||
                  query.difficulty ||
                  query.unscheduled,
              )}
              archived={query.view === "archived"}
              onClear={() => {
                setSearchValue("");
                const next = new URLSearchParams();
                if (query.view === "archived") next.set("view", "archived");
                if (requestedScheduleDate) next.set("scheduleDate", requestedScheduleDate);
                parametersRef.current = next;
                setSearchParameters(next, { replace: true });
              }}
              returnContext={returnContext}
            />
          )}
        </section>
      </div>

      {collectionDialog ? (
        <CollectionNameDialog
          collection={collectionDialog}
          onDismiss={() => setCollectionDialog(null)}
        />
      ) : null}
      {deleteDialog ? (
        <DeleteCollectionDialog
          collection={deleteDialog}
          selected={query.collectionId === deleteDialog.id}
          fallbackFocus={collectionFilter}
          onDeleted={() => {
            if (query.collectionId === deleteDialog.id) {
              updateParameters({ collectionId: null });
            }
          }}
          onDismiss={() => setDeleteDialog(null)}
        />
      ) : null}
      {archiveDialog ? (
        <ArchiveActivityDialog activity={archiveDialog} onDismiss={() => setArchiveDialog(null)} />
      ) : null}
      {activityDeleteDialog ? (
        <DeleteActivityDialog
          activity={activityDeleteDialog}
          fallbackFocus={archivedActivitiesButton}
          onDismiss={() => setActivityDeleteDialog(null)}
        />
      ) : null}
    </AppShell>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
  selectRef,
}: {
  label: string;
  value: string;
  options: ReadonlyArray<readonly [string, string]>;
  onChange: (value: string) => void;
  selectRef?: React.RefObject<HTMLSelectElement | null>;
}) {
  return (
    <label className="filter-select">
      <span className="visually-hidden">{label}</span>
      <select
        ref={selectRef}
        value={value}
        aria-label={label}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue || "all"} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
      <ChevronDown size={14} aria-hidden="true" />
    </label>
  );
}

function ActivityRow({
  activity,
  returnContext,
  requestedScheduleDate,
  isArchivedView,
  isNewlyCreated,
  isPending,
  onArchive,
  onRestore,
  onDelete,
}: {
  activity: ActivitySummary;
  returnContext: string;
  requestedScheduleDate: string | null;
  isArchivedView: boolean;
  isNewlyCreated: boolean;
  isPending: boolean;
  onArchive: () => void;
  onRestore: () => void;
  onDelete: () => void;
}) {
  const FormatIcon =
    activity.format === "video" ? CirclePlay : activity.format === "book" ? BookOpenText : FileText;
  return (
    <article
      className={`activity-row${isArchivedView ? " activity-row-archived" : ""}${isNewlyCreated ? " activity-row-newly-created" : ""}`}
      aria-label={isNewlyCreated ? `Newly created activity: ${activity.title}` : undefined}
      data-newly-created={isNewlyCreated ? "true" : undefined}
    >
      <div className={`activity-row-art format-${activity.format}`} aria-hidden="true">
        <FormatIcon size={25} strokeWidth={1.6} />
      </div>
      <div className="activity-row-copy">
        <Link
          className="activity-row-main"
          to={`/activities/${activity.id}`}
          state={{
            activityTitle: activity.title,
            activityReturnTo: createActivityReturnState(returnContext)?.activityReturnTo,
            libraryReturnTo: returnContext,
          }}
          aria-label={`Open ${activity.title}`}
        >
          <h3>{activity.title}</h3>
        </Link>
        <div className="activity-row-actions">
          {!isArchivedView ? (
            <Link
              to={`/activities/${activity.id}/schedule${
                requestedScheduleDate ? `?date=${encodeURIComponent(requestedScheduleDate)}` : ""
              }`}
              state={{ libraryReturnTo: returnContext }}
              aria-label={`Schedule ${activity.title}`}
            >
              <CalendarPlus size={17} aria-hidden="true" />
            </Link>
          ) : null}
          <Link
            to={`/activities/${activity.id}/edit`}
            state={{ libraryReturnTo: returnContext }}
            aria-label={`Edit ${activity.title}`}
          >
            <SquarePen size={17} aria-hidden="true" />
          </Link>
          <button
            type="button"
            aria-label={`${isArchivedView ? "Restore" : "Archive"} ${activity.title}`}
            disabled={isPending}
            onClick={isArchivedView ? onRestore : onArchive}
          >
            {isArchivedView ? (
              <RotateCcw size={17} aria-hidden="true" />
            ) : (
              <Archive size={17} aria-hidden="true" />
            )}
          </button>
          {isArchivedView ? (
            <button
              type="button"
              aria-label={`Delete ${activity.title} permanently`}
              disabled={isPending}
              onClick={onDelete}
            >
              <Trash2 size={17} aria-hidden="true" />
            </button>
          ) : null}
          {activity.hasFlashcards ? <FlashcardIndicator /> : null}
        </div>
      </div>
      <ActivityRatingRail interest={activity.interest} difficulty={activity.difficulty} />
    </article>
  );
}

function ActivityRatingRail({
  interest,
  difficulty,
}: {
  interest: number | null;
  difficulty: number | null;
}) {
  return (
    <aside className="activity-rating-rail" aria-label="Activity ratings">
      <ActivityStarRating label="Interesting" value={interest} />
      <ActivityStarRating label="Difficulty" value={difficulty} />
    </aside>
  );
}

function ActivityStarRating({ label, value }: { label: string; value: number | null }) {
  return (
    <div className={`activity-star-rating activity-star-rating-${label.toLowerCase()}`}>
      <span className="activity-rating-badge">{label}</span>
      <span className="activity-rating-stars" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star key={star} data-filled={value !== null && star <= value ? "true" : undefined} />
        ))}
      </span>
      <span className="sr-only">
        {value === null ? "Unrated" : `${value.toString()} out of 5 stars`}
      </span>
    </div>
  );
}

function LibraryLoading() {
  return (
    <div className="activity-list" aria-label="Loading library" aria-busy="true">
      {[1, 2, 3].map((item) => (
        <div className="activity-row skeleton-row" key={item}>
          <span />
          <span />
        </div>
      ))}
    </div>
  );
}

function LibraryErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="empty-state" role="alert">
      <BookOpen size={26} />
      <h3>Your library could not be loaded</h3>
      <p>Your activities are safe. Try loading them again.</p>
      <button type="button" className="soft-button" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

function ActivityArchiveError({
  title,
  action,
  onRetry,
}: {
  title: string;
  action: "archive" | "restore";
  onRetry: () => void;
}) {
  return (
    <div className="library-action-error" role="alert">
      <div>
        <strong>
          “{title}” could not be {action === "archive" ? "archived" : "restored"}.
        </strong>
        <span>Your activity is safe and unchanged.</span>
      </div>
      <button type="button" className="soft-button" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

function LibraryEmptyState({
  hasCriteria,
  archived,
  onClear,
  returnContext,
}: {
  hasCriteria: boolean;
  archived: boolean;
  onClear: () => void;
  returnContext: string;
}) {
  return (
    <div className="empty-state">
      <Search size={26} />
      <h3>
        {hasCriteria
          ? "No activities match"
          : archived
            ? "Nothing is archived"
            : "Your library is ready"}
      </h3>
      <p>
        {hasCriteria
          ? "Try a different phrase, collection, or filter."
          : archived
            ? "Activities you archive will stay safely here."
            : "Create your first activity when you are ready."}
      </p>
      {hasCriteria ? (
        <button type="button" className="soft-button" onClick={onClear}>
          Clear search and filters
        </button>
      ) : archived ? null : (
        <Link
          className="soft-button"
          to="/activities/new"
          state={{ libraryReturnTo: returnContext }}
        >
          <Plus size={16} /> New activity
        </Link>
      )}
    </div>
  );
}

function CollectionNameDialog({
  collection,
  onDismiss,
}: {
  collection: CollectionSummary;
  onDismiss: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();
  const [name, setName] = useState(collection.name);
  const [fieldError, setFieldError] = useState("");
  const mutation = useMutation({
    mutationFn: () => renameCollection(collection.id, name),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: libraryQueryRoot }),
        queryClient.invalidateQueries({ queryKey: todayQueryRoot }),
      ]);
      dialog.current?.close();
    },
    onError: (error) => {
      setFieldError(
        error instanceof ApiError
          ? (error.problem?.fieldErrors?.name?.[0] ?? error.problem?.title ?? error.message)
          : "The collection could not be saved.",
      );
    },
  });
  useModalDialog(dialog, onDismiss);

  return (
    <dialog ref={dialog} className="calm-dialog" aria-labelledby="collection-name-dialog-title">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = name.trim();
          if (!trimmed) {
            setFieldError("Enter a collection name.");
            return;
          }
          setFieldError("");
          mutation.mutate();
        }}
      >
        <span className="section-kicker">Rename collection</span>
        <h2 id="collection-name-dialog-title">Choose a new name</h2>
        <label>
          Collection name
          <input
            autoFocus
            value={name}
            maxLength={80}
            aria-invalid={Boolean(fieldError)}
            aria-describedby={fieldError ? "collection-name-error" : undefined}
            onChange={(event) => {
              setName(event.target.value);
              setFieldError("");
            }}
          />
        </label>
        <div className="dialog-message">
          {fieldError ? (
            <p id="collection-name-error" className="field-error">
              {fieldError}
            </p>
          ) : null}
        </div>
        <div className="dialog-actions">
          <button type="button" className="soft-button" onClick={() => dialog.current?.close()}>
            Cancel
          </button>
          <button type="submit" className="primary-button" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Save name"}
          </button>
        </div>
      </form>
    </dialog>
  );
}

function DeleteCollectionDialog({
  collection,
  selected,
  fallbackFocus,
  onDeleted,
  onDismiss,
}: {
  collection: CollectionSummary;
  selected: boolean;
  fallbackFocus: React.RefObject<HTMLElement | null>;
  onDeleted: () => void;
  onDismiss: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();
  const activityCount = collection.activeCount + collection.archivedCount;
  const mutation = useMutation({
    mutationFn: () => deleteCollection(collection.id, activityCount > 0),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: libraryQueryRoot }),
        queryClient.invalidateQueries({ queryKey: todayQueryRoot }),
      ]);
      onDeleted();
      dialog.current?.close();
    },
  });
  useModalDialog(dialog, onDismiss, fallbackFocus);

  return (
    <dialog ref={dialog} className="calm-dialog" aria-labelledby="delete-collection-dialog-title">
      <div>
        <span className="section-kicker">Delete collection</span>
        <h2 id="delete-collection-dialog-title">Delete “{collection.name}”?</h2>
        <p>
          {activityCount > 0
            ? `${activityCount.toString()} activities will remain safely in your Library as Uncollected. Archived activities stay archived and no activity content is deleted.`
            : "This collection is empty. Deleting it will not remove any activities."}
        </p>
        {selected ? <p>Your view will return to All activities.</p> : null}
        {mutation.isError ? (
          <p className="field-error" role="alert">
            The collection could not be deleted. Please try again.
          </p>
        ) : null}
        <div className="dialog-actions">
          <button type="button" className="soft-button" onClick={() => dialog.current?.close()}>
            Keep collection
          </button>
          <button
            type="button"
            className="danger-button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending
              ? "Deleting…"
              : activityCount > 0
                ? "Delete collection and keep activities"
                : "Delete collection"}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function DeleteActivityDialog({
  activity,
  fallbackFocus,
  onDismiss,
}: {
  activity: ActivitySummary;
  fallbackFocus: React.RefObject<HTMLButtonElement | null>;
  onDismiss: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => permanentlyDeleteActivity(activity.id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: libraryQueryRoot }),
        queryClient.invalidateQueries({ queryKey: todayQueryRoot }),
      ]);
      dialog.current?.close();
    },
  });
  useModalDialog(dialog, onDismiss, fallbackFocus);

  return (
    <dialog
      ref={dialog}
      className="calm-dialog"
      aria-labelledby="delete-activity-dialog-title"
      aria-describedby="delete-activity-dialog-description"
      onCancel={(event) => {
        if (mutation.isPending) event.preventDefault();
      }}
    >
      <div>
        <span className="section-kicker">Delete learning activity</span>
        <h2 id="delete-activity-dialog-title">Permanently delete “{activity.title}”?</h2>
        <p id="delete-activity-dialog-description">
          This learning activity and its learning history will be deleted. This cannot be undone,
          and the activity will not be recoverable.
        </p>
        {mutation.isError ? (
          <p className="field-error" role="alert">
            The activity could not be deleted. It is still safely archived. Please try again.
          </p>
        ) : null}
        <div className="dialog-actions">
          <button
            type="button"
            className="soft-button"
            disabled={mutation.isPending}
            onClick={() => dialog.current?.close()}
          >
            Keep activity
          </button>
          <button
            type="button"
            className="danger-button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "Deleting…" : "Delete permanently"}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function useModalDialog(
  reference: React.RefObject<HTMLDialogElement | null>,
  onDismiss: () => void,
  fallbackFocus?: React.RefObject<HTMLElement | null>,
) {
  const dismiss = useRef(onDismiss);
  const opener = useRef<HTMLElement | null>(null);
  dismiss.current = onDismiss;

  useEffect(() => {
    const dialog = reference.current;
    if (!dialog) return;
    const activeElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (activeElement && !dialog.contains(activeElement)) {
      opener.current = activeElement;
    }
    let restoredFocus = false;
    const restoreFocus = () => {
      if (restoredFocus) return;
      restoredFocus = true;
      window.requestAnimationFrame(() => {
        const target = opener.current?.isConnected ? opener.current : fallbackFocus?.current;
        target?.focus();
      });
    };
    const handleClose = () => {
      dismiss.current();
      restoreFocus();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          "button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href]",
        ),
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    dialog.showModal();
    dialog.addEventListener("close", handleClose);
    dialog.addEventListener("keydown", handleKeyDown);
    return () => {
      dialog.removeEventListener("close", handleClose);
      dialog.removeEventListener("keydown", handleKeyDown);
    };
  }, [fallbackFocus, reference]);
}

function parseLibraryQuery(parameters: URLSearchParams): LibraryQuery {
  const format = activityFormatSchema.safeParse(parameters.get("format"));
  const difficultyParameter = parameters.get("difficulty");
  const difficultyValue = Number(difficultyParameter);
  const difficulty =
    difficultyParameter === "unrated"
      ? "unrated"
      : Number.isInteger(difficultyValue) && difficultyValue >= 1 && difficultyValue <= 5
        ? difficultyValue
        : undefined;
  const view = parameters.get("view") === "archived" ? "archived" : "active";
  const collectionId = parameters.get("collectionId") ?? undefined;
  const unscheduled = parameters.get("unscheduled") === "true";
  return {
    search: parameters.get("search")?.slice(0, 120) ?? "",
    view,
    ...(collectionId ? { collectionId } : {}),
    ...(format.success ? { format: format.data } : {}),
    ...(difficulty ? { difficulty } : {}),
    ...(unscheduled ? { unscheduled: true } : {}),
  };
}

function formatLabel(format: ActivityFormat): string {
  return format === "other" ? "Resource" : `${format.charAt(0).toUpperCase()}${format.slice(1)}`;
}

function activityCreatedId(value: unknown): string | null {
  if (typeof value !== "object" || value === null || !("activityCreated" in value)) return null;
  const created = value.activityCreated;
  if (
    typeof created !== "object" ||
    created === null ||
    !("id" in created) ||
    typeof created.id !== "string"
  ) {
    return null;
  }
  return created.id;
}
